import { Order } from './order.models';

/**
 * Pure helpers for the `order` domain.
 */

/** Postal code prefixes considered to belong to the Canary Islands. */
const CANARIAS_PREFIXES = ['35', '38'] as const;

/** Postal code prefixes considered to belong to the Balearic Islands. */
const BALEARES_PREFIXES = ['07'] as const;

/**
 * Determines whether a postal code change implies a tax/shipping zone change
 * (Canarias <-> Península or Baleares <-> Península).
 *
 * @param oldPostalCode Previous postal code.
 * @param newPostalCode New postal code.
 * @returns `true` if the geographic billing zone has changed.
 */
export function zoneHasChanged(oldPostalCode: string, newPostalCode: string): boolean {
  const oldPrefix = (oldPostalCode ?? '').substring(0, 2);
  const newPrefix = (newPostalCode ?? '').substring(0, 2);

  const oldIsCanarias = (CANARIAS_PREFIXES as readonly string[]).includes(oldPrefix);
  const newIsCanarias = (CANARIAS_PREFIXES as readonly string[]).includes(newPrefix);
  const oldIsBaleares = (BALEARES_PREFIXES as readonly string[]).includes(oldPrefix);
  const newIsBaleares = (BALEARES_PREFIXES as readonly string[]).includes(newPrefix);

  return (oldIsCanarias !== newIsCanarias) || (oldIsBaleares !== newIsBaleares);
}

/**
 * Opens the order's invoice URL in a new browser tab, when available.
 *
 * @param order Order containing the `invoiceUrl` to download.
 * @returns `true` if a window was opened, `false` if there was no invoice URL.
 */
export function downloadInvoice(order: Pick<Order, 'invoiceUrl'>): boolean {
  if (!order?.invoiceUrl) return false;
  window.open(order.invoiceUrl, '_blank', 'noopener noreferrer');
  return true;
}
